/**
 * ScheduleDraftFlightForm — ScheduleDraft 航班新增/编辑表单组件。
 *
 * 本组件消费引用数据选项、航路 by-leg 查询与字段联动重算规则，负责把用户
 * 编辑语义呈现为 Antd Form 字段。payload 组装仍由 services 层处理，表单不
 * 直接了解后端写入契约。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → hooks/useScheduleDraftFlightFormOptions.js (引用选项), lib/flightRecalculationRules.js (字段联动), services/scheduleDraftPayloadBuilders.js (payload)
 */

import React, { useEffect, useMemo, useRef } from "react";
import { Alert, AutoComplete, Form, Input, Select, Switch } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import AirportSelector from "../../../../components/common/AirportSelector";
import { UTCTimePicker } from "../../../../components/DateTimeInput";
import useScheduleDraftFlightFormOptions from "../hooks/useScheduleDraftFlightFormOptions";
import { useFlightRoutesByLeg } from "../../../../hooks/useReferenceData";
import { applyRecalculationRules } from "../lib/flightRecalculationRules";
import { normalizeAirportCode, routeMatchesCurrentAircraft } from "../lib/routeUtils";
import { isRecommendedStcType, normalizeStcTypeValue, RECOMMENDED_STC_TYPE_OPTIONS, STC_CUSTOM_VALUE_WARNING } from "../constants/scheduleDraftOptions";

dayjs.extend(utc);

const airportFilter = (input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase());

/**
 * 渲染可复用的航班编辑表单。
 * @param {Object} props - 表单属性。
 * @param {boolean} props.editable - 是否允许编辑字段。
 * @param {boolean} props.showReadonlyHint - 是否展示参考轨只读提示。
 * @param {(referenceData: Object) => void} [props.onReferenceDataChange] - 向提交层暴露当前航段航路引用数据。
 * @returns {React.ReactElement} 航班表单内容。
 */
const ScheduleDraftFlightForm = ({ editable = true, showReadonlyHint = false, onReferenceDataChange }) => {
    const form = Form.useFormInstance();
    const previousLegRef = useRef({ departureAirport: null, arrivalAirport: null, initialized: false });
    const { airports, aircrafts, aircraftMap, isLoading } = useScheduleDraftFlightFormOptions(true);
    const departureAirport = Form.useWatch("departure_airport", form);
    const arrivalAirport = Form.useWatch("arrival_airport", form);
    const aircraftReg = Form.useWatch("aircraft_reg", form);
    const selectedRouteId = Form.useWatch("flight_route_id", form);
    const stcType = Form.useWatch("stc_type", form);
    const { routes, isLoading: loadingFlightRoutes } = useFlightRoutesByLeg(departureAirport, arrivalAirport);
    const currentAircraft = aircraftMap?.[aircraftReg] || null;
    const normalizedStcType = normalizeStcTypeValue(stcType);
    const showStcWarning = normalizedStcType !== "" && !isRecommendedStcType(normalizedStcType);

    const { flightRouteOptions, routesById } = useMemo(() => {
        const cache = {};
        const options = routes
            .filter((option) => option.route?.is_active !== false)
            .filter((option) => routeMatchesCurrentAircraft(option.route, {
                aircraft_reg: aircraftReg,
                aircraft_type: currentAircraft?.aircraft_type,
                aircraft_type_id: currentAircraft?.aircraft_type_id,
            }, {
                aircraftsByRegistration: aircraftMap,
            }))
            .map((option) => {
                cache[option.value] = option.route;
                return {
                    value: option.value,
                    label: option.label,
                    title: option.label,
                };
            });

        return { flightRouteOptions: options, routesById: cache };
    }, [aircraftMap, aircraftReg, currentAircraft, routes]);

    const routeReferenceData = useMemo(() => {
        const routeList = Object.values(routesById);
        return {
            routes: routeList,
            routesById,
            routesByLeg: departureAirport && arrivalAirport
                ? { [`${normalizeAirportCode(departureAirport)}:${normalizeAirportCode(arrivalAirport)}`]: routeList }
                : {},
            aircraftsByRegistration: aircraftMap,
        };
    }, [aircraftMap, arrivalAirport, departureAirport, routesById]);

    useEffect(() => {
        onReferenceDataChange?.(routeReferenceData);
    }, [onReferenceDataChange, routeReferenceData]);

    useEffect(() => {
        if (loadingFlightRoutes) {
            return;
        }

        const previousLeg = previousLegRef.current;
        if (!previousLeg.initialized) {
            const hasLegValue = departureAirport !== undefined || arrivalAirport !== undefined;
            if (hasLegValue) {
                previousLegRef.current = { departureAirport, arrivalAirport, initialized: true };
            }
            return;
        }

        const legChanged = previousLeg.initialized
            && (previousLeg.departureAirport !== departureAirport || previousLeg.arrivalAirport !== arrivalAirport);
        previousLegRef.current = { departureAirport, arrivalAirport, initialized: true };

        if (!legChanged) {
            return;
        }

        const currentRouteId = form.getFieldValue("flight_route_id");
        const routeStillValid = currentRouteId && flightRouteOptions.some((option) => option.value === currentRouteId);
        if (routeStillValid) {
            return;
        }

        const changedAirportField = previousLeg.departureAirport !== departureAirport ? "departure_airport" : "arrival_airport";
        const result = applyRecalculationRules(changedAirportField, {
            newValue: changedAirportField === "departure_airport" ? departureAirport : arrivalAirport,
            previousValue: changedAirportField === "departure_airport" ? previousLeg.departureAirport : previousLeg.arrivalAirport,
            currentFlight: {
                departure_airport: departureAirport,
                arrival_airport: arrivalAirport,
                flight_route_id: currentRouteId,
                std: form.getFieldValue("std"),
                sta: form.getFieldValue("sta"),
                block_time_minutes: form.getFieldValue("block_time_minutes"),
                aircraft_reg: aircraftReg,
                aircraft_type: currentAircraft?.aircraft_type,
                aircraft_type_id: currentAircraft?.aircraft_type_id,
            },
            referenceData: { routesById, aircraftsByRegistration: aircraftMap },
        });

        form.setFieldsValue(result.patches);
    }, [aircraftMap, aircraftReg, arrivalAirport, currentAircraft, departureAirport, flightRouteOptions, form, loadingFlightRoutes, routesById]);

    const handleRouteChange = (routeId) => {
        const result = applyRecalculationRules("flight_route_id", {
            newValue: routeId,
            currentFlight: {
                std: form.getFieldValue("std"),
                sta: form.getFieldValue("sta"),
                block_time_minutes: form.getFieldValue("block_time_minutes"),
            },
            referenceData: { routesById },
        });

        form.setFieldsValue({
            flight_route_id: routeId,
            ...result.patches,
        });
    };

    const handleStdChange = (value) => {
        const result = applyRecalculationRules("std", {
            newValue: value,
            currentFlight: {
                std: value,
                sta: form.getFieldValue("sta"),
                block_time_minutes: form.getFieldValue("block_time_minutes"),
                flight_route_id: selectedRouteId,
            },
            referenceData: { routesById },
        });

        if (Object.keys(result.patches).length > 0) {
            form.setFieldsValue(result.patches);
        }
    };

    return (
        <>
            {showReadonlyHint ? (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="当前选中的是参考轨条目"
                    description="参考轨仅用于对照，不允许直接修改。请切回当前编辑轨再执行写操作。"
                />
            ) : null}

            <Form.Item name="flight_number" label="航班号" rules={[{ required: true, message: "请输入航班号" }]}>
                <Input placeholder="例如：CA123" maxLength={12} disabled={!editable} />
            </Form.Item>

            <Form.Item name="aircraft_reg" label="飞机注册号" rules={[{ required: true, message: "请选择飞机" }]}>
                <AircraftSelector valueType="registration" displayMode="detailed" aircrafts={aircrafts} loading={isLoading} placeholder="选择飞机" allowClear={false} disabled={!editable} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="departure_airport" label="出发机场" rules={[{ required: true, message: "请选择出发机场" }]}>
                <AirportSelector valueType="iata_code" displayMode="detailed" airports={airports} loading={isLoading} placeholder="选择出发机场" disabled={!editable} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="arrival_airport" label="到达机场" rules={[{ required: true, message: "请选择到达机场" }]}>
                <AirportSelector valueType="iata_code" displayMode="detailed" airports={airports} loading={isLoading} placeholder="选择到达机场" disabled={!editable} style={{ width: "100%" }} />
            </Form.Item>

            {!selectedRouteId ? <Alert type="warning" showIcon message="当前航班未绑定航路" description="请选择航路以使用航路 BH 自动计算 STA，并让保存 payload 携带 flight_route_id。" /> : null}

            <Form.Item name="flight_route_id" label="航路" tooltip="航路选项来自 flight-routes/by-leg；选中后会用航路 BH 保持 STD 不变并重算 STA。">
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={flightRouteOptions}
                    loading={loadingFlightRoutes}
                    placeholder={departureAirport && arrivalAirport ? "选择航路" : "请先选择 DEP/ARR"}
                    disabled={!editable || !departureAirport || !arrivalAirport}
                    filterOption={airportFilter}
                    onChange={handleRouteChange}
                />
            </Form.Item>

            <Form.Item name="block_time_minutes" hidden>
                <Input />
            </Form.Item>
            <Form.Item name="min_block_time_minutes" hidden>
                <Input />
            </Form.Item>

            <Form.Item name="std" label="STD (UTC)" rules={[{ required: true, message: "请选择 STD" }]}>
                <UTCTimePicker style={{ width: "100%" }} format="HH:mm" showDate={true} minuteStep={5} disabled={!editable} onChange={handleStdChange} />
            </Form.Item>

            <Form.Item name="sta" label="STA (UTC)" rules={[{ required: true, message: "请选择 STA" }]}>
                <UTCTimePicker style={{ width: "100%" }} format="HH:mm" showDate={true} minuteStep={5} disabled={!editable} />
            </Form.Item>

            <Form.Item name="stc_type" label="航班性质" normalize={normalizeStcTypeValue} validateStatus={showStcWarning ? "warning" : undefined} help={showStcWarning ? STC_CUSTOM_VALUE_WARNING : undefined}>
                <AutoComplete
                    allowClear
                    options={RECOMMENDED_STC_TYPE_OPTIONS}
                    placeholder="输入或选择 STC"
                    disabled={!editable}
                    filterOption={(inputValue, option) => String(option?.value || "").includes(normalizeStcTypeValue(inputValue))}
                />
            </Form.Item>

            <Form.Item name="is_fixed" label="锁定状态" valuePropName="checked">
                <Switch checkedChildren="已锁定" unCheckedChildren="可编辑" disabled={!editable} />
            </Form.Item>

            <Form.Item name="remarks" label="备注">
                <Input.TextArea rows={3} maxLength={500} placeholder="记录当前航班的备注说明" disabled={!editable} />
            </Form.Item>
        </>
    );
};

export default ScheduleDraftFlightForm;
